// web/frontend/components/BillingGate.jsx
import React from "react";

export default function BillingGate({ children }) {
  // Cache to avoid flicker for already-subscribed shops
  const initialActive =
    (typeof window !== "undefined" &&
      localStorage.getItem("vv_last_sub_active") === "1") ||
    false;

  const [loading, setLoading] = React.useState(true);
  const [hasActive, setHasActive] = React.useState(initialActive);
  const [error, setError] = React.useState("");

  const ranRef = React.useRef(false); // prevent StrictMode double-run

  // Query params / persisted context
  const params = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const shopFromQuery = params.get("shop") || "";
  const hostFromQuery = params.get("host") || "";

  const appSlugEnv = import.meta.env.VITE_SHOPIFY_APP_SLUG || "";
  const requireBilling =
    String(import.meta.env.VITE_SUBSCRIPTION_REQUIRED || "1") !== "0";

  React.useEffect(() => {
    try {
      if (hostFromQuery) localStorage.setItem("vv_host", hostFromQuery);
      if (shopFromQuery) localStorage.setItem("vv_shop", shopFromQuery);
      if (appSlugEnv) localStorage.setItem("vv_app_slug", appSlugEnv);

      const handle =
        (shopFromQuery || localStorage.getItem("vv_shop") || "")
          .replace(/\.myshopify\.com$/i, "");
      if (handle) localStorage.setItem("vv_store_handle", handle);
    } catch {}
  }, [hostFromQuery, shopFromQuery, appSlugEnv]);

  const shop =
    shopFromQuery || localStorage.getItem("vv_shop") || ""; // full shop domain
  const storeHandle =
    localStorage.getItem("vv_store_handle") ||
    shop.replace(/\.myshopify\.com$/i, "");
  const appSlug = appSlugEnv || localStorage.getItem("vv_app_slug") || "";

  const pricingUrl =
    storeHandle && appSlug
      ? `https://admin.shopify.com/store/${encodeURIComponent(
          storeHandle
        )}/charges/${encodeURIComponent(appSlug)}/pricing_plans`
      : "";

  const runCheck = React.useCallback(async () => {
    if (!requireBilling) {
      setHasActive(true);
      setLoading(false);
      return;
    }
    if (!shop) {
      setError("Missing shop param.");
      setHasActive(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const query = `
        query BillingGateCheck {
          currentAppInstallation {
            activeSubscriptions {
              id
              status
              test
              name
              createdAt
              lineItems { id }
            }
          }
        }
      `;

      const res = await fetch(`/api/graphql?shop=${encodeURIComponent(shop)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
        credentials: "include",
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(`GraphQL HTTP ${res.status}: ${t}`);
      }

      const json = await res.json();
      const subs =
        json?.data?.currentAppInstallation?.activeSubscriptions || [];
      const active = subs.some(
        (s) => String(s?.status).toUpperCase() === "ACTIVE"
      );

      setHasActive(active);
      try {
        localStorage.setItem("vv_last_sub_active", active ? "1" : "0");
      } catch {}
    } catch (e) {
      console.error("[BillingGate] failed to query subscriptions:", e);
      setError("Load failed");
      setHasActive(false);
      try {
        localStorage.setItem("vv_last_sub_active", "0");
      } catch {}
    } finally {
      setLoading(false);
    }
  }, [requireBilling, shop]);

  React.useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    runCheck();
  }, [runCheck]);

  // RENDER

  // If billing isn’t required or we confirmed active, render page
  if (!requireBilling || hasActive) {
    return children;
  }

  // While loading, show neutral placeholder (no main page, no paywall)
  if (loading) {
    return (
      <div
        style={{
          minHeight: "50vh",
          display: "grid",
          placeItems: "center",
          padding: 24,
        }}
      >
        <div style={{ color: "#6b7280", fontSize: 14 }}>
          Checking subscription…
        </div>
      </div>
    );
  }

  // Not loading and still no active → show plan card
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: 520,
          maxWidth: "92vw",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          Choose a plan to continue
        </div>
        <div
          style={{
            color: "#6b7280",
            fontSize: 14,
            marginBottom: 18,
            lineHeight: 1.45,
          }}
        >
          Once you select a Shopify plan, you will be able to continue.
        </div>

        {!pricingUrl ? (
          <div
            style={{
              color: "#B42318",
              background: "#FEF3F2",
              border: "1px solid #FEE4E2",
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 14,
              fontSize: 13,
            }}
          >
            Missing app slug or store handle; cannot open pricing.
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <a
            href={pricingUrl || "#"}
            target="_top"
            rel="noopener"
            onClick={(e) => {
              if (!pricingUrl) e.preventDefault();
              else console.log("[BillingGate] top-level navigate →", pricingUrl);
            }}
            style={{
              textDecoration: "none",
              padding: "10px 16px",
              borderRadius: 6,
              background: pricingUrl ? "#2E6BFF" : "#93A0B8",
              color: "#fff",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 160,
              cursor: pricingUrl ? "pointer" : "not-allowed",
              boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
            }}
          >
            Choose a plan
          </a>

        </div>

        <div style={{ marginTop: 14, color: "#6b7280", fontSize: 12 }}>
          {error ? error : "No active subscription found yet."}
        </div>
      </div>
    </div>
  );
}
